import "../../transit-map.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { useMemo } from "react";
import { useParams } from "react-router";

import { useGetLoadTracking } from "~/features/loads/hooks/use-get-load-tracking.ts";
import { useGetPublicTracking } from "~/features/track-shipment/hooks/use-get-public-tracking.ts";
import { buildMapPings } from "~/shared/components/tracking-map/build-map-pings.ts";
import { TrackingMap } from "~/shared/components/tracking-map/tracking-map.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";

import { formatStopDate } from "../../format-stop-date.ts";

export const MapContent = () => {
	const params = useParams();
	const { isInternal } = useDeploymentType();
	const poNumber = Number(params["poNumber"]);
	const validPoNumber =
		Number.isFinite(poNumber) && poNumber > 0 ? poNumber : null;

	// Internal users hit the employee-only tracking endpoint via useGetLoadTracking
	// (tracking/internal/:poNumber). External/customer users must use the public
	// tracking endpoint — the internal one is blocked at the edge (WAF/AFD 403).
	// Only the relevant hook is enabled; the other is disabled by passing a null
	// PO number.
	const internalTracking = useGetLoadTracking(
		isInternal ? validPoNumber : null,
	);
	const publicTracking = useGetPublicTracking(
		isInternal ? null : validPoNumber,
	);

	const updates = isInternal
		? internalTracking.updates
		: publicTracking.trackingUpdates;
	const stops = isInternal ? internalTracking.stops : publicTracking.stops;
	const status = isInternal ? internalTracking.status : publicTracking.status;
	const isLoading = isInternal
		? internalTracking.isLoading
		: publicTracking.isLoading;

	const mapPings = useMemo(
		() => buildMapPings(updates, stops),
		[updates, stops],
	);

	const lastStop = useMemo(
		() =>
			[...stops].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).at(-1),
		[stops],
	);

	const latestUpdate = useMemo(
		() =>
			[...updates].sort(
				(a, b) => Date.parse(b.sortDate) - Date.parse(a.sortDate),
			)[0],
		[updates],
	);

	const renderMapContent = () => {
		if (isLoading) {
			return (
				<div className={"default-text"}>
					<Loader type={"infinite-spinner"} />
				</div>
			);
		}
		if (mapPings.length === 0) {
			return <div className={"default-text"}>No tracking data available</div>;
		}
		return <TrackingMap pings={mapPings} />;
	};

	return (
		<>
			<div className={"map-container"}>{renderMapContent()}</div>
			<div className={"tracking-summary"}>
				<div className={"tracking-label"}>Current Location:</div>
				<div className={"tracking-label"}>Estimated Delivery:</div>
				<div className={"tracking-label"}>Current Delivery Status:</div>
				<div>{latestUpdate?.location ?? "Unknown"}</div>
				<div>{formatStopDate(lastStop?.stopDate ?? null)}</div>
				<div>{status ?? "Unknown"}</div>
			</div>
		</>
	);
};
